import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/lib/brand";

export const Navbar = ({ onQuoteClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
          : "bg-gradient-to-b from-black/30 to-transparent"
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
          >
            <img
              src="/logo.webp"
              alt="Bloc & Brush"
              className="h-10 lg:h-11 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[15px] font-medium transition-colors relative py-2 ${
                    scrolled
                      ? (isActive ? "text-[#13294B]" : "text-[#1C1C1C]/70 hover:text-[#13294B]")
                      : (isActive ? "text-white" : "text-white/75 hover:text-white")
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <span className={`absolute -bottom-0.5 left-0 right-0 h-px ${scrolled ? "bg-[#13294B]" : "bg-white"}`} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BRAND.phoneHref}
              data-testid="navbar-call-mobile"
              className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${scrolled ? "border-[#13294B]/15 text-[#13294B]" : "border-white/30 text-white"}`}
              aria-label="Call us"
            >
              <Phone size={18} />
            </a>
            <button
              onClick={onQuoteClick}
              data-testid="navbar-quote-btn"
              className={`hidden md:inline-flex items-center rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 ${
                scrolled
                  ? "bg-[#13294B] hover:bg-[#0E1F3A] text-white"
                  : "bg-white text-[#13294B] hover:bg-white/90"
              }`}
            >
              Get Free Quote
            </button>
            <button
              onClick={() => setOpen(!open)}
              data-testid="navbar-menu-toggle"
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full border transition-colors ${scrolled ? "border-[#13294B]/15 text-[#13294B]" : "border-white/30 text-white"}`}
              aria-label="Toggle menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-black/5"
        >
          <div className="container mx-auto px-6 py-6 max-w-7xl flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-black/5 ${
                    isActive ? "text-[#13294B]" : "text-[#1C1C1C]/80"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onQuoteClick?.();
              }}
              data-testid="mobile-quote-btn"
              className="mt-5 bg-[#13294B] text-white rounded-full px-6 py-3 text-sm font-medium"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
